// File: src/components/admin/PromoCodesListView.js
// Promotion codes admin list view. Mirrors PlansView.js.
//
// Lists all promo codes (archived split into a collapsed accordion), provides
// "New code" creation, and delegates per-code editing to PromoCodeEditor.
// Each active card exposes a "Copy link" button that copies the public
// redemption URL to the clipboard.
//
// Env:
//   REACT_APP_PROMO_BASE_URL — base URL of the public premium app where promo
//   links resolve. Set per-environment by deploy-admin.yml (prod →
//   https://ads.defoai.com, staging → https://ads-staging.defoai.com). The
//   fallback default is the prod app URL. Link format: `${PROMO_BASE_URL}/promo/${code}`
//   — the dedicated golden-ticket landing (PromoLandingView). The legacy
//   `/?promo=${code}` query form still works (App.jsx captures it), but the
//   path form is the canonical share URL we hand out.

import React, { useEffect, useState } from 'react';
import {
    Box, Stack, Typography, Button, CircularProgress, Alert, Accordion,
    AccordionSummary, AccordionDetails, Chip, Snackbar,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';

import { mainApi } from '../../services/mainApi';
import PromoCodeEditor from './promos/PromoCodeEditor';
import CreatePromoCodeDialog from './promos/CreatePromoCodeDialog';

const PROMO_BASE_URL = process.env.REACT_APP_PROMO_BASE_URL || 'https://ads.defoai.com';

export function buildPromoLink(code) {
    return `${PROMO_BASE_URL}/promo/${encodeURIComponent(code)}`;
}

export default function PromoCodesListView() {
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [copied, setCopied] = useState(null);
    // planId → displayName, used to render a friendly plan label on each card.
    // Best-effort: if the plans fetch fails the editor falls back to the raw id.
    const [planNameById, setPlanNameById] = useState({});

    async function reload() {
        try {
            setError(null);
            const data = await mainApi.listPromoCodes();
            setPromos(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        }
    }

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        mainApi.listPromoCodes()
            .then((data) => { if (!cancelled) setPromos(Array.isArray(data) ? data : []); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        // Resolve plan ids → display names for the per-card chip. Non-blocking
        // and non-fatal: a failure just leaves the chips showing raw ids.
        mainApi.listPlans()
            .then((plans) => {
                if (cancelled) return;
                const map = {};
                for (const p of (Array.isArray(plans) ? plans : [])) {
                    map[p.id] = p.displayName || p.name || p.id;
                }
                setPlanNameById(map);
            })
            .catch(() => { /* best-effort; chip falls back to raw id */ });
        return () => { cancelled = true; };
    }, []);

    function handlePromoChanged() {
        // A save or archive can flip status, which moves the card between the
        // active and archived sections; reload to keep the split accurate.
        reload();
    }

    function handleCreated(promo) {
        setPromos(ps => [...ps, promo]);
    }

    async function handleCopyLink(code) {
        const link = buildPromoLink(code);
        try {
            await navigator.clipboard.writeText(link);
            setCopied(code);
        } catch (e) {
            setError(`Could not copy link: ${e.message}`);
        }
    }

    if (loading) {
        return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }

    const active = promos.filter(p => p.status !== 'archived');
    const archived = promos.filter(p => p.status === 'archived');

    function renderCard(p) {
        return (
            <Box key={p.code}>
                <PromoCodeEditor promo={p} onPromoChanged={handlePromoChanged} planNameById={planNameById} />
                <Box sx={{ mt: 1 }}>
                    <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        onClick={() => handleCopyLink(p.code)}
                        data-testid={`promo-copy-link-${p.code}`}
                    >
                        Copy link
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box data-testid="promos-view" sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Promo Codes</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Create and manage promotion codes. Copy a code's public redemption link
                        to share it. Archived codes can no longer be redeemed.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setCreateOpen(true)}
                    data-testid="promo-new"
                >
                    New code
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            <Stack spacing={3}>
                {active.map(renderCard)}
                {active.length === 0 && (
                    <Alert severity="info">No active promo codes. Create one above.</Alert>
                )}
            </Stack>

            {archived.length > 0 && (
                <Accordion sx={{ mt: 4 }} data-testid="archived-promos-accordion">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>Archived</Typography>
                        <Chip size="small" label={archived.length} sx={{ ml: 1.5 }} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={3}>
                            {archived.map(renderCard)}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            <CreatePromoCodeDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={handleCreated}
            />

            <Snackbar
                open={!!copied}
                autoHideDuration={2500}
                onClose={() => setCopied(null)}
                message={copied ? `Copied link for ${copied}` : ''}
                data-testid="promo-copied-snackbar"
            />
        </Box>
    );
}
